import { usePortalContainer } from '@/common/hooks/use-portal-container'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import InitErrorIndicator from '@/common/components/init-error-indicator/init-error-indicator'
import RowStack from '@common-components/common/row-stack/RowStack'
import { accessFormPopupActions } from '@common-reducers/AccessFormPopupReducer'
import { btLayoutSidebarActions } from '@common-reducers/BtLayoutSidebarReducer'
import { openBulkDashboard, setActiveBulkView, setActiveTab } from '@common-reducers/CampaignReducer'
import { campaignsMemoryRouter } from '@common-views/campaigns-redesign/campaigns-routes'
import { createCampaignLaunchHandlers } from './campaign-launch'
import { isContentScriptAlive } from '@common/services/orphan-guard'
import { readSurfaceGeometry, watchSurfaceMount } from '@common-services/ui-stall-reporter'
import { DBReminderThunks } from '@common-reducers/DBServiceThunks'
import { mainSliceActions, setStartChatWithPhoneNumberOpen } from '@common-reducers/MainReducer'
import { taskActions } from '@common-reducers/TasksReducer'
import type { CommonRootState } from '@common/types/common-root-state-type'
import { selectUser } from '@common-reducers/UserSelectors'
import { toggleLabelsFeature } from '@common-reducers/UserThunks'
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import { selectUserId } from '@common/reducers/UserSelectors'
import { IconButton } from '@mui/material'
import { Stack } from '@mui/system'
import { Bell, Megaphone, MessageSquareText, Settings, Tag, UserPlus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useStore } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { namespace } from '@common-config/constants'
import ManageExtensionMenu from './manage-extension-menu'
import LanguageSwitcher from '../language-switcher/language-switcher'
import DebugDiagnosticsButton from './debug-diagnostics-button'
import ExtTrialChip from '@common-components/ext-trial/ext-trial-chip'
import { AgentGlyph } from '@common-components/mascot/AgentGlyph'

// const blueStopColors = ['#6CD1F1', '#269BE9']; // Original blue gradient stops
export const greenStopColors = ['#76C8A1', '#34A853']; // Green gradient stops
export const redStopColors = ['#FA8072', '#E9403A']; // Red gradient stops

export default function ExtensionTopButtonBar() {
    const { t } = useTranslation(namespace)
    const dispatch = useAppDispatch()
    const store = useStore<CommonRootState>()
    const [modelType, setModalType] = useState<string>('')
    const [open, setOpen] = useState<boolean>(false)

    const localPortalContainer = usePortalContainer();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);

    useEffect(() => {
        // Try to find the global portal container (LabelsModals) to avoid z-index/overflow issues in chat rows
        // This is necessary because rows in virtual lists often have stacking contexts that trap absolute elements
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setPortalContainer(globalPortal as HTMLElement);
        }
    }, []);


    const user = useAppSelector(selectUser);
    const isLabelsEnabled = !user?.isLabelsDisabled;

    // Reminders logic
    const { isOpen: isSidebarOpen, componentToShow } = useAppSelector((state) => state.BtLayoutSidebarReducer);
    const reminders = useAppSelector((state) => state.DBRemindersReducer?.entities || []);

    // Count overdue reminders (active, not completed, and due date has passed)
    // isActive: false means completed, isActive: undefined/true means active
    const now = new Date();
    const activeRemindersCount = Object.values(reminders).filter(
        (r: any) => {
            if (!r || r.deleted || r.isActive === false) return false;
            const dueDate = r.dueAt ? new Date(r.dueAt) : null;
            return dueDate && dueDate < now;
        }
    ).length;

    useEffect(() => {
        // Fetch all reminders for the user
        dispatch(DBReminderThunks.find({
            $sort: { dueAt: 1 } // Sort by due date ascending
        }));
    }, [dispatch]);

    // Check if Reminders sidebar is currently open
    const isRemindersOpen = isSidebarOpen && componentToShow === 'Reminders';

    const isQuickAgentsOpen = isSidebarOpen && componentToShow === 'QuickAgents';

    const toggleQuickAgents = () => {
        if (isQuickAgentsOpen) {
            dispatch(btLayoutSidebarActions.closeSidebar());
        } else {
            dispatch(btLayoutSidebarActions.openQuickAgentsWithScope('all'));
        }
    };

    const toggleReminders = () => {
        if (isRemindersOpen) {
            dispatch(btLayoutSidebarActions.closeSidebar());
        } else {
            dispatch(btLayoutSidebarActions.openWithComponent('Reminders'));
        }
    };

    const handleButtonClick = (menuType: string) => {
        if (!user) {
            dispatch(accessFormPopupActions.setStatus('Signup'))
            return;
        }

        setModalType(menuType)
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
        setModalType('')
    };

    // #651: if the campaigns overlay hasn't mounted ~3s after the click, report why.
    const watchCampaignMount = (action: string) => watchSurfaceMount(action, () => ({
        stateOpen: store.getState().CampaignReducer.open,
        ...readSurfaceGeometry(document.getElementById('campaigns-view')?.shadowRoot?.querySelector('[data-bt-surface="campaigns"]') ?? null),
    }))

    // #651: the click wiring lives in createCampaignLaunchHandlers (unit-tested); FE#318 order kept.
    const { manageCampaigns: manageCampaignEntities, newCampaign: launchNewCampaign } = createCampaignLaunchHandlers({
        watchMount: watchCampaignMount,
        closeTasksPanel: () => dispatch(taskActions.setIsTasksPanelOpen(false)),
        setManageSurface: () => {
            dispatch(setActiveTab("manageCampaigns"))
            dispatch(setActiveBulkView("campaignManagementView"))
        },
        setNewCampaignSurface: () => dispatch(setActiveBulkView('newCampaignView')),
        openPanel: () => dispatch(openBulkDashboard()),
        navigate: (path) => campaignsMemoryRouter.navigate(path),
        handleClose,
        isAlive: isContentScriptAlive,
    })

    return (
        <Stack direction='row' sx={{
            gap: '10px',
            height: '47px',
            p: 0,
            alignItems: 'center',
            justifyContent: 'space-between',
            // width: "480px",
        }}>
            <RowStack spacing={2}>
                {/* Init Error Indicator - shows warning when initialization has errors */}
                {/* `display: contents` so this wrapper generates NO box of its own.
                    These three wrappers exist only so ShadowFeatureGuard can target
                    them by `data-bt-feature`; their children render null most of the
                    time. As real flex items they were 0px wide but still consumed the
                    RowStack's 16px gap, which pushed the first visible button 16px in
                    and left it misaligned with the `+` in the row below. The guard's
                    `display: none !important` still overrides `contents`, so hiding
                    keeps working. */}
                <div data-bt-feature="topBtnInitError" style={{ display: 'contents' }}>
                  <InitErrorIndicator />
                </div>

                <TooltipProvider>
                    <div data-bt-feature="topBtnLogo">
                    <DropdownMenu
                        open={open && modelType === 'manageExtensionMessagingMenu'}
                        onOpenChange={(isOpen) => {
                            if (!isOpen) {
                                handleClose();
                            } else {
                                if (!user) {
                                    dispatch(accessFormPopupActions.setStatus('Signup'));
                                    return;
                                }
                                setModalType('manageExtensionMessagingMenu');
                                setOpen(true);
                            }
                        }}
                    >
                        <DropdownMenuTrigger asChild>
                            <IconButton
                                sx={{
                                    background: 'linear-gradient(-135deg, #42c3ec, #016eb8)',
                                    color: 'white',
                                    width: 38,
                                    height: 38,
                                    minHeight: 'auto',
                                    boxShadow: 'none',
                                    border: "none",
                                    transition: 'background 0.3s ease',
                                    ':hover': {
                                        background: 'linear-gradient(-135deg, #6dd1f1, var(--tasks-button-hover-bg))',
                                    }
                                }}
                            >
                                <svg fill='white' width="30" height="30" viewBox={"0 0 24 24"}>
                                    <g>
                                        <path d={'M17.3 6.3a.9959.9959 0 0 0-1.41 0l-5.64 5.64 1.41 1.41L17.3 7.7c.38-.38.38-1.02 0-1.4zm4.24-.01-9.88 9.88-3.48-3.47a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l4.18 4.18c.39.39 1.02.39 1.41 0L22.95 7.71c.39-.39.39-1.02 0-1.41h-.01c-.38-.4-1.01-.4-1.4-.01zM1.12 14.12 5.3 18.3c.39.39 1.02.39 1.41 0l.7-.7-4.88-4.9a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.03 0 1.42z'} />
                                    </g>
                                </svg>
                            </IconButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="start"
                            container={portalContainer || localPortalContainer}
                            className="w-[250px] p-0 border-none bg-popover text-popover-foreground shadow-xl z-[9999]"
                            style={{
                                padding: 0,
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-surface)',
                                boxShadow: '0 16px 40px var(--modal-shadow)',
                                backdropFilter: 'blur(8px)',
                            }}
                        >
                            <ManageExtensionMenu handleClose={handleClose} />
                        </DropdownMenuContent>
                    </DropdownMenu>
                    </div>

                    {user && (
                        <>
                            {/* AI Agents — opens the Quick Agents panel (toggles it closed when open). */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={toggleQuickAgents}
                                        aria-label={t('navigation.aiAgents')}
                                        data-testid="top-btn-ai-agents"
                                        className={`w-[38px] h-[38px] rounded-full flex items-center justify-center transition-colors relative hover:bg-gray-100 hover:text-[#016eb8] ${isQuickAgentsOpen ? 'text-[#016eb8] bg-blue-50' : 'text-gray-500'}`}
                                    >
                                        <AgentGlyph size={20} />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    <p>{t('navigation.aiAgents')}</p>
                                </TooltipContent>
                            </Tooltip>

                            <div data-bt-feature="topBtnOpenMessages">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={(e) => dispatch(mainSliceActions.toggleCalendarView())}
                                        className="w-[38px] h-[38px] rounded-full flex items-center justify-center transition-colors relative text-gray-500 hover:bg-gray-100 hover:text-[#016eb8]"
                                    >
                                        <MessageSquareText size={20} className="currentColor" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    <p>{t('topMenu.openMessages')}</p>
                                </TooltipContent>
                            </Tooltip>
                            </div>

                            <div data-bt-feature="topBtnLaunchCampaign">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={launchNewCampaign}
                                        className="w-[38px] h-[38px] rounded-full flex items-center justify-center transition-colors relative text-gray-500 hover:bg-gray-100 hover:text-[#016eb8]"
                                    >
                                        <Megaphone size={20} className="currentColor" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    <p>{t('topMenu.launchNewCampaign')}</p>
                                </TooltipContent>
                            </Tooltip>
                            </div>

                            <div data-bt-feature="topBtnOpenChat">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={(e) => dispatch(setStartChatWithPhoneNumberOpen(true))}
                                        className="w-[38px] h-[38px] rounded-full flex items-center justify-center transition-colors relative text-gray-500 hover:bg-gray-100 hover:text-[#016eb8]"
                                    >
                                        <UserPlus size={20} className="currentColor" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    <p>{t('topMenu.openChat')}</p>
                                </TooltipContent>
                            </Tooltip>
                            </div>

                            <div data-bt-feature="topBtnToggleLabels">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={() => dispatch(toggleLabelsFeature())}
                                        className={`w-[38px] h-[38px] rounded-full flex items-center justify-center transition-all relative ${isLabelsEnabled
                                                ? 'bg-[#016eb8]/10 text-[#016eb8] border-2 border-[#016eb8] hover:bg-[#016eb8]/20'
                                                : 'text-gray-400 border-2 border-gray-300 hover:bg-gray-100 hover:text-gray-500 hover:border-gray-400'
                                            }`}
                                    >
                                        <Tag size={18} />
                                        {isLabelsEnabled && (
                                            <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#016eb8] rounded-full border border-white" />
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    <p>{isLabelsEnabled ? t('topMenu.disableLabels') : t('topMenu.enableLabels')}</p>
                                </TooltipContent>
                            </Tooltip>
                            </div>

                            <div data-bt-feature="topBtnManageCampaigns">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={manageCampaignEntities}
                                        className="w-[38px] h-[38px] rounded-full flex items-center justify-center transition-colors relative text-gray-500 hover:bg-gray-100 hover:text-[#016eb8]"
                                    >
                                        <Settings size={20} className="currentColor" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    <p>{t('topMenu.manageCampaigns')}</p>
                                </TooltipContent>
                            </Tooltip>
                            </div>

                            {/* Reminders Button */}
                            <div data-bt-feature="topBtnReminders">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={toggleReminders}
                                        className={`w-[38px] h-[38px] rounded-full flex items-center justify-center transition-colors relative ${isRemindersOpen
                                                ? 'bg-gray-100 text-[#016eb8]'
                                                : 'text-gray-500 hover:bg-gray-100 hover:text-[#016eb8]'
                                            }`}
                                    >
                                        <Bell size={20} className="currentColor" />
                                        {activeRemindersCount > 0 && (
                                            <span
                                                className="absolute top-0 right-0 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold text-white rounded-full px-1"
                                                style={{
                                                    background: 'linear-gradient(135deg, #f87171, #dc2626)',
                                                    boxShadow: '0 1px 3px rgba(220, 38, 38, 0.4)',
                                                    transform: 'translate(25%, -25%)'
                                                }}
                                            >
                                                {activeRemindersCount > 99 ? '99+' : activeRemindersCount}
                                            </span>
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    <p>{t('topMenu.reminders')}{activeRemindersCount > 0 ? ` (${activeRemindersCount})` : ''}</p>
                                </TooltipContent>
                            </Tooltip>
                            </div>

                            {/* #693: persistent trial chip; renders nothing without a live trial. */}
                            <ExtTrialChip />

                            {/* Debug Mode Button - self-contained, with its own dialog */}
                            <div data-bt-feature="topBtnDebugDiag" style={{ display: 'contents' }}>
                              <DebugDiagnosticsButton />
                            </div>
                        </>
                    )}
                </TooltipProvider>


                {/* <ToggleDarkMode /> */}
                {/* <MenuIconButton
                    onClick={(e) => dispatch(setSetNewStatusPopupOpen(true))}
                    Icon={<MapsUgc />}
                    alt="Set status"
                /> */}
            </RowStack>
        </Stack>
    )
}

const BlueticksIconButton = ({ onClick }: { onClick: React.MouseEventHandler<HTMLButtonElement> }) => {
    const { t } = useTranslation(namespace);
    const userId = useAppSelector(selectUserId);
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <IconButton onClick={onClick} sx={{
                        background: 'linear-gradient(-135deg, #42c3ec, #016eb8)', // Apply gradient here
                        color: 'white',
                        width: 38, // Adjusting size
                        height: 38,
                        minHeight: 'auto', // Override minimum height
                        '.MuiSvgIcon-root': { // Target the icon inside the Fab
                            fontSize: '25px', // Icon size
                        },
                        boxShadow: 'none',
                        border: "none",
                        transition: 'background 0.3s ease',
                        ':hover': { // Change background on hover
                            background: 'linear-gradient(-135deg, #6dd1f1, var(--tasks-button-hover-bg))', // Update these values for your hover gradient
                        }
                    }}>
                        <svg fill='white' width="30" height="30" viewBox={"0 0 24 24"}>
                            <g>
                                <path d={'M17.3 6.3a.9959.9959 0 0 0-1.41 0l-5.64 5.64 1.41 1.41L17.3 7.7c.38-.38.38-1.02 0-1.4zm4.24-.01-9.88 9.88-3.48-3.47a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l4.18 4.18c.39.39 1.02.39 1.41 0L22.95 7.71c.39-.39.39-1.02 0-1.41h-.01c-.38-.4-1.01-.4-1.4-.01zM1.12 14.12 5.3 18.3c.39.39 1.02.39 1.41 0l.7-.7-4.88-4.9a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.03 0 1.42z'} />
                            </g>
                        </svg>
                    </IconButton>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                    <p>{!userId ? t('topMenu.login') : t('topMenu.accountOptions')}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}
